import { Fragment, useContext, useState } from 'react';
import { useModal } from 'react-modal-hook';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';

import LoginContext from 'utils/gatsby/context/login-status';

import { APP_URL, LOGIN_URL } from 'components/forms/constants';
import { TippyTopBanner } from 'components/globals/tippy-top-banner';
import FontIcon from 'components/icons';
import Link from 'components/link/link';
import ScheduleDemoModal from 'components/modal/schedule-demo/schedule-demo-modal';
import Signups from 'components/modal/signups';
import { MenuDropDown } from 'components/navigation/partials/menu-drop-down';

import './styles/main.scss';

type NavigationMainProps = {
  customHubspotMeetingUrl?: string,
  isBlog?: boolean,
  trialLength?: number
}

export default function NavigationMain({ isBlog, customHubspotMeetingUrl, trialLength }:NavigationMainProps) {

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openMenu, setOpenMenu] = useState('');
  const { loggedIn } = useContext(LoginContext);
  const [showScheduleDemoModal, hideScheduleDemoModal] = useModal(() => (
    <ScheduleDemoModal isOpen onHide={ hideScheduleDemoModal } customHubspotMeetingUrl={ customHubspotMeetingUrl } />
  ));

  const data = useStaticQuery(graphql`
{
  allWordpressWpMainGlobalMenu {
    nodes {
      title
      url
      description
      classes
    }
  }
  allWordpressAcfOptions {
    edges {
      node {
        options {
          nav_logo {
            source_url
          }
          login_banner_text
          tippy_top_banner {
            enabled
            custom_calendar {
              text
              calendar_text
              calendar_url
              details
              details_url
            }
            use_markup
            markup
          }
        }
      }
    }
  }
}`);

  const wiwLogo = data.allWordpressAcfOptions.edges[0].node.options.nav_logo.source_url;
  
  // menu items are in WPAdmin Appearance > Menus > main global menu
  const menuItems = data.allWordpressWpMainGlobalMenu.nodes;
  const featuresTitle = menuItems[0].title;
  const featuresItems = menuItems.slice(1, 5);
  const industriesTitle = menuItems[5].title;
  const industriesItems = menuItems.slice(6, 16);
  const industriesBottomItem = menuItems[16];
  const resourcesTitle = menuItems[17].title;
  const resourcesItems = menuItems.slice(18, 22);
  const createAccount = menuItems[22];
  const pricing = menuItems[23];
  const scheduleDemo = menuItems[24];
  const login = menuItems[25];
  const tippyTopBanner = data.allWordpressAcfOptions.edges[0].node.options.tippy_top_banner;

  return (
    <Fragment>
      <TippyTopBanner { ...tippyTopBanner } />
      <nav
        className={ `navigation-main ${ isBlog ? 'is-blog' : '' }` }
        aria-label="Primary Navigation"
        data-section="mainNavigation"
      >
        <div className="container-navigation">
          <div className="navigation-main-globals">

            <Link className="logo-link" to="/" title="When I Work - Home">
              <img
                className="logo"
                src={ wiwLogo }
                alt="When I Work Logo - To Home Page"
                width="167"
                height="31"
              />
            </Link>

            <Link
              to="/blog"
              className={ `navigation-main-globals-blog-link is-rounded ${!isBlog && 'is-hidden'}` }
              title="When I Work - Blog Home"
            >
              Blog
            </Link>
          </div>

          {/* sign up form -- mobile */}
          <div className="is-hidden-tablet-nav start-trial">
            <Signups
              uniqueId="mainNav"
              longPlaceholder={ true }
              fieldLayout="row"
              buttonClasses="button is-signup-button is-rounded"
              showModalButton={ true }
              ctaVariation='Nav CTA + modal'
            />
          </div>

          {/* mobile menu button */}
          <button className="main-navicon is-hidden-tablet-nav"
            aria-expanded={ showMobileMenu }
            onClick={ () => setShowMobileMenu(cur => !cur)  }>
            <span className="is-sr-only">Open Menu</span>
            {!showMobileMenu ? <FontIcon icon="more-horizontal-lines" /> : <FontIcon icon="close" />}
          </button>

          <ul className={ classNames('menu-group', { 'is-hidden-mobile-nav': !showMobileMenu }) }>

            {/* Features Menu */}
            <MenuDropDown
              title={ featuresTitle }
              items={ featuresItems }
              openMenu={ openMenu }
              setOpenMenu={ setOpenMenu }
            />

            {/* Industries Menu */}
            <MenuDropDown
              title={ industriesTitle }
              items={ industriesItems }
              bottomItem={ industriesBottomItem }
              openMenu={ openMenu }
              setOpenMenu={ setOpenMenu }
            />

            {/* Resources Menu */}
            <MenuDropDown
              title={ resourcesTitle }
              items={ resourcesItems }
              openMenu={ openMenu }
              setOpenMenu={ setOpenMenu }
            />

            {/* Pricing link */}
            <li className="menu-list-item pricing">
              <Link
                className="menu-item"
                title="When I Work - Pricing"
                to={ pricing.url }
              >
                {pricing.title}
              </Link>
            </li>

            {/* sign up link -- mobile */}
            <li className="menu-link is-hidden-tablet-nav">
              <Link
                className="button is-rounded menu-item"
                to={ createAccount.url }
              >
                {createAccount.title}
              </Link>
            </li>

            {/* sign up form -- desktop */}
            { !isBlog &&
            <li className="start-trial is-hidden-mobile-nav">
              <Signups
                uniqueId="mainNav"
                longPlaceholder={ true }
                showInitFields={ false }
                fieldLayout="row"
                buttonClasses="button is-signup-button is-rounded"
                showModalButton={ true }
                ctaVariation='Nav CTA + modal'
                trialLength={ trialLength }
              />
            </li>
            }

            {/* Schedule demo */}
            <li className="menu-link demo">
              <button
                title="When I Work - Request a Demo"
                type="button"
                className="button is-rounded menu-item"
                onClick={ showScheduleDemoModal }
              >
                {scheduleDemo.title}
              </button>
            </li>

            {/* Log in */}
            <li className="menu-link login">
              <a
                className="button is-rounded menu-item"
                href={ loggedIn ? APP_URL : LOGIN_URL }
              >
                { loggedIn ? 'Dashboard' : login.title }
                <FontIcon icon="arrow-right" />
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </Fragment>
  );
}
